/**
 * Converts ELK orthogonal layout edge sections into SVG path strings compatible with EdgeRenderer.
 */

import { buildPathWithCorners } from '../utils/pathBuilders';
import { CORNER_RADIUS } from '../utils/constants';

function distanceSq(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/**
 * Deduplicate consecutive points (ELK may repeat section boundaries).
 * @param {Array<{x:number,y:number}>} points
 */
function dedupeConsecutivePoints(points) {
  if (points.length < 2) return points;
  const out = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    if (distanceSq(points[i], out[out.length - 1]) > 0.25) {
      out.push(points[i]);
    }
  }
  return out;
}

/**
 * Build an SVG `d` string from ELK edge `sections` (orthogonal routing).
 * @param {Array} sections - ELK edge.sections
 * @returns {string}
 */
export function elkSectionsToPathD(sections) {
  if (!sections || sections.length === 0) return '';

  const points = [];
  for (const sec of sections) {
    if (sec.startPoint) {
      const p = { x: sec.startPoint.x, y: sec.startPoint.y };
      if (points.length === 0 || distanceSq(p, points[points.length - 1]) > 0.25) {
        points.push(p);
      }
    }
    if (Array.isArray(sec.bendPoints)) {
      for (const bp of sec.bendPoints) {
        const p = { x: bp.x, y: bp.y };
        if (points.length === 0 || distanceSq(p, points[points.length - 1]) > 0.25) {
          points.push(p);
        }
      }
    }
    if (sec.endPoint) {
      const p = { x: sec.endPoint.x, y: sec.endPoint.y };
      if (points.length === 0 || distanceSq(p, points[points.length - 1]) > 0.25) {
        points.push(p);
      }
    }
  }

  const cleaned = dedupeConsecutivePoints(points);
  if (cleaned.length < 2) return '';
  return buildPathWithCorners(cleaned, CORNER_RADIUS);
}

/**
 * @param {object} layoutedGraph - ELK layout result graph (root or subgraph)
 * @returns {Record<string, string>} edge id -> SVG path d
 */
export function buildEdgePathMapFromElkGraph(layoutedGraph) {
  const map = {};
  if (!layoutedGraph?.edges) return map;
  for (const edge of layoutedGraph.edges) {
    if (!edge?.id) continue;
    const d = elkSectionsToPathD(edge.sections);
    if (d) map[edge.id] = d;
  }
  return map;
}

/**
 * Extract the logical corner waypoints of a path built by `buildPathWithCorners`.
 *
 * Unlike `extractWaypoints` (pathParsing), this understands the arc fillets (`A` commands):
 * an arc's start point (pushed by the preceding `L`) sits CORNER_RADIUS short of the true
 * corner, so the arc is collapsed back into the exact corner point. Rebuilding a path from
 * arc-start points instead produces ~radius-sized staircase kinks with inverted-looking
 * fillets ("dents").
 *
 * @param {string} pathD
 * @returns {Array<{x:number,y:number}>}
 */
export function extractCornerWaypoints(pathD) {
  if (!pathD) return [];
  const tokens = pathD.replace(/,/g, ' ').split(/\s+/).filter(Boolean);
  const pts = [];
  let i = 0;
  while (i < tokens.length) {
    const cmd = tokens[i];
    if (cmd === 'M' || cmd === 'L') {
      const x = parseFloat(tokens[i + 1]);
      const y = parseFloat(tokens[i + 2]);
      if (Number.isFinite(x) && Number.isFinite(y)) pts.push({ x, y });
      i += 3;
    } else if (cmd === 'A') {
      // A rx ry rot large-arc sweep x y — a 90° fillet between two orthogonal segments.
      const ex = parseFloat(tokens[i + 6]);
      const ey = parseFloat(tokens[i + 7]);
      if (Number.isFinite(ex) && Number.isFinite(ey)) {
        if (pts.length >= 2) {
          const arcStart = pts[pts.length - 1];
          const before = pts[pts.length - 2];
          const incomingVertical =
            Math.abs(arcStart.x - before.x) <= Math.abs(arcStart.y - before.y);
          // True corner = arc start advanced to the arc end along the incoming axis.
          pts[pts.length - 1] = incomingVertical
            ? { x: arcStart.x, y: ey }
            : { x: ex, y: arcStart.y };
        } else {
          pts.push({ x: ex, y: ey });
        }
      }
      i += 8;
    } else if (cmd === 'Q') {
      const ex = parseFloat(tokens[i + 3]);
      const ey = parseFloat(tokens[i + 4]);
      if (Number.isFinite(ex) && Number.isFinite(ey)) pts.push({ x: ex, y: ey });
      i += 5;
    } else {
      i += 1;
    }
  }
  return pts;
}

/**
 * Shift an SVG path `d` by (dx, dy) by rebuilding waypoints (preserves orthogonal shape).
 * @param {string} pathD
 * @param {number} dx
 * @param {number} dy
 * @returns {string}
 */
export function translatePathD(pathD, dx, dy) {
  if (!pathD || (dx === 0 && dy === 0)) return pathD || '';
  const wp = extractCornerWaypoints(pathD);
  if (wp.length < 2) return pathD;
  const shifted = wp.map((p) => ({ x: p.x + dx, y: p.y + dy }));
  return buildPathWithCorners(shifted, CORNER_RADIUS);
}

/**
 * True when an orthogonal path passes through any of the given node rectangles.
 *
 * Used to validate ELK/warped paths against cards at their LIVE positions: ELK only
 * guarantees clearance for the auto layout, so a manually moved card can end up sitting
 * on top of a path (its own edges' warped routes, or an unrelated edge it was dragged
 * onto). Segments are axis-aligned, so a bounding-box overlap per segment is exact.
 *
 * @param {string} pathD
 * @param {Array<{id:string, pos:{x:number,y:number}, dims?:{width:number,height:number}}>} nodes
 * @param {Set<string>|null} excludeIds — node ids the path legitimately touches (its endpoints)
 * @param {number} [pad] — extra clearance around each card counted as overlap
 */
export function pathIntersectsAnyNodeRect(pathD, nodes, excludeIds = null, pad = 2) {
  if (!pathD || !nodes || nodes.length === 0) return false;
  const wp = extractCornerWaypoints(pathD);
  if (wp.length < 2) return false;

  for (const node of nodes) {
    if (!node?.pos || node.id == null) continue;
    if (excludeIds && excludeIds.has(node.id)) continue;
    const left = node.pos.x - pad;
    const right = node.pos.x + (node.dims?.width || 0) + pad;
    const top = node.pos.y - pad;
    const bottom = node.pos.y + (node.dims?.height || 0) + pad;
    for (let i = 1; i < wp.length; i += 1) {
      const a = wp[i - 1];
      const b = wp[i];
      if (Math.max(a.x, b.x) < left || Math.min(a.x, b.x) > right) continue;
      if (Math.max(a.y, b.y) < top || Math.min(a.y, b.y) > bottom) continue;
      return true;
    }
  }
  return false;
}

const WARP_EPS = 0.01;
/** Segments with both axes moving more than this are diagonals needing a corner insert. */
const WARP_ORTHO_EPS = 0.75;
/** Minimum straight run out of / into a port so paths never hug a node border. */
const WARP_MIN_PORT_STUB = 12;

function isZeroDelta(d) {
  return Math.abs(d.x) <= WARP_EPS && Math.abs(d.y) <= WARP_EPS;
}

function segmentIsVertical(a, b) {
  return Math.abs(b.x - a.x) <= Math.abs(b.y - a.y);
}

/** Direction (+1/-1) of the a→b segment along `axis` ('x'|'y'); 0 when degenerate. */
function segmentSign(a, b, axis) {
  return Math.sign(b[axis] - a[axis]);
}

/**
 * Route a fresh minimal orthogonal path between the moved endpoints, preserving the
 * original port exit/entry axes and directions. Returns null when the endpoints no longer
 * admit that shape (e.g. target dragged behind the source) — caller keeps the warped shape.
 *
 * Simple base shapes (straight / L / Z) are the vast majority of ELK edges; re-deriving
 * them avoids dragging the original ELK lane along ("pointless travel" through where the
 * node used to be).
 */
function rebuildSimpleOrthogonalPath(origPts, s, e) {
  const n = origPts.length;
  const exitVertical = segmentIsVertical(origPts[0], origPts[1]);
  const entryVertical = segmentIsVertical(origPts[n - 2], origPts[n - 1]);
  const exitAxis = exitVertical ? 'y' : 'x';
  const entryAxis = entryVertical ? 'y' : 'x';
  const exitSign = segmentSign(origPts[0], origPts[1], exitAxis);
  const entrySign = segmentSign(origPts[n - 2], origPts[n - 1], entryAxis);
  if (exitSign === 0 || entrySign === 0) return null;

  if (exitVertical === entryVertical) {
    // Z-shape (or straight): stub out, cross at the midline, stub in.
    if (exitSign !== entrySign) return null;
    if ((e[exitAxis] - s[exitAxis]) * exitSign < 2 * WARP_MIN_PORT_STUB) return null;
    const mid = (s[exitAxis] + e[exitAxis]) / 2;
    return exitVertical
      ? [s, { x: s.x, y: mid }, { x: e.x, y: mid }, e]
      : [s, { x: mid, y: s.y }, { x: mid, y: e.y }, e];
  }

  // L-shape: one corner at the crossing of the two port axes.
  if ((e[exitAxis] - s[exitAxis]) * exitSign < WARP_MIN_PORT_STUB) return null;
  if ((e[entryAxis] - s[entryAxis]) * entrySign < WARP_MIN_PORT_STUB) return null;
  const corner = exitVertical ? { x: s.x, y: e.y } : { x: e.x, y: s.y };
  return [s, corner, e];
}

/**
 * Push a port-adjacent waypoint so the end segment keeps at least a small straight stub in
 * its original direction (a moved node can otherwise flip the segment backwards into the
 * card — a visible dent right at the port).
 *
 * The connector lane the adjacent waypoint belongs to (the perpendicular segment sharing
 * its `axis` coordinate) is moved as a whole; moving only the adjacent point would create
 * an out-and-back spike that collinear cleanup collapses, erasing the stub again.
 *
 * @param {'x'|'y'} axis — axis of the end segment
 * @param {number} sign — direction from the endpoint toward its adjacent waypoint
 */
function enforcePortStub(pts, endIndex, adjacentIndex, axis, sign) {
  if (sign === 0) return;
  const run = (pts[adjacentIndex][axis] - pts[endIndex][axis]) * sign;
  if (run >= WARP_MIN_PORT_STUB) return;

  const oldCoord = pts[adjacentIndex][axis];
  const newCoord = pts[endIndex][axis] + sign * WARP_MIN_PORT_STUB;
  const step = adjacentIndex > endIndex ? 1 : -1;
  for (let i = adjacentIndex; i >= 1 && i <= pts.length - 2; i += step) {
    if (Math.abs(pts[i][axis] - oldCoord) > WARP_ORTHO_EPS) break;
    pts[i] = { ...pts[i], [axis]: newCoord };
  }
}

/**
 * Re-anchor an ELK edge path whose endpoint nodes moved away from the auto-layout
 * coordinates the path was computed for.
 *
 * Simple shapes (straight / L / Z) are re-routed from scratch between the live port
 * points; longer ELK detours keep their shape with each end translated by its own node's
 * delta (live position − auto position) and orthogonality repaired where the two
 * adjustments meet.
 *
 * This is how manually placed / dragged cards keep package-style edge routing: ELK's
 * layered algorithm cannot route edges around pinned nodes (its "interactive" mode still
 * recomputes every coordinate), so the auto-layout path is adapted to the real positions
 * instead of asking ELK to route at them.
 *
 * @param {string} pathD — base ELK path in canvas space
 * @param {{x:number,y:number}} sourceDelta — source node live − auto
 * @param {{x:number,y:number}} targetDelta — target node live − auto
 * @returns {string|null} warped path, or null when the path cannot be warped
 */
export function warpPathToNodeDeltas(pathD, sourceDelta, targetDelta) {
  if (!pathD) return null;
  const ds = sourceDelta;
  const dt = targetDelta;
  if (!ds || !Number.isFinite(ds.x) || !Number.isFinite(ds.y)) return null;
  if (!dt || !Number.isFinite(dt.x) || !Number.isFinite(dt.y)) return null;
  if (isZeroDelta(ds) && isZeroDelta(dt)) return pathD;
  if (Math.abs(ds.x - dt.x) <= WARP_EPS && Math.abs(ds.y - dt.y) <= WARP_EPS) {
    return translatePathD(pathD, ds.x, ds.y) || null;
  }

  const wp = extractCornerWaypoints(pathD);
  if (wp.length < 2) return null;
  const pts = wp.map((p) => ({ x: p.x, y: p.y }));
  const n = pts.length;
  const s = { x: pts[0].x + ds.x, y: pts[0].y + ds.y };
  const e = { x: pts[n - 1].x + dt.x, y: pts[n - 1].y + dt.y };

  // Simple base shapes get a fresh minimal route at the live endpoints.
  // (For n === 2 the exit and entry segment are the same, which rebuild handles naturally.)
  if (n <= 4) {
    const rebuilt = rebuildSimpleOrthogonalPath(pts, s, e);
    if (rebuilt) {
      const d = buildPathWithCorners(dedupeConsecutivePoints(rebuilt), CORNER_RADIUS);
      if (d) return d;
    }
  }

  const firstVertical = segmentIsVertical(pts[0], pts[1]);
  const lastVertical = segmentIsVertical(pts[n - 2], pts[n - 1]);
  const exitAxis = firstVertical ? 'y' : 'x';
  const entryAxis = lastVertical ? 'y' : 'x';
  const exitSign = segmentSign(pts[0], pts[1], exitAxis);
  const entrySign = segmentSign(pts[n - 1], pts[n - 2], entryAxis);

  if (n === 2) {
    // Single segment whose ends move apart: split into an orthogonal jog at the midpoint.
    const jog = firstVertical
      ? [s, { x: s.x, y: (s.y + e.y) / 2 }, { x: e.x, y: (s.y + e.y) / 2 }, e]
      : [s, { x: (s.x + e.x) / 2, y: s.y }, { x: (s.x + e.x) / 2, y: e.y }, e];
    const d = buildPathWithCorners(dedupeConsecutivePoints(jog), CORNER_RADIUS);
    return d || null;
  }

  // Keep the ELK detour shape: move each endpoint by its node's delta and drag the free
  // coordinate of the adjacent waypoint along so the end segment stays axis-aligned.
  pts[0] = s;
  if (firstVertical) pts[1].x += ds.x;
  else pts[1].y += ds.y;

  pts[n - 1] = e;
  if (lastVertical) pts[n - 2].x += dt.x;
  else pts[n - 2].y += dt.y;

  // A large move can flip an end segment backwards into the card — keep a straight stub.
  enforcePortStub(pts, 0, 1, exitAxis, exitSign);
  enforcePortStub(pts, n - 1, n - 2, entryAxis, entrySign);

  // The adjustments can leave diagonals where they collide — insert corners.
  const repaired = [pts[0]];
  let prevVertical = firstVertical;
  for (let i = 1; i < pts.length; i += 1) {
    const a = repaired[repaired.length - 1];
    const b = pts[i];
    if (Math.abs(b.x - a.x) > WARP_ORTHO_EPS && Math.abs(b.y - a.y) > WARP_ORTHO_EPS) {
      // Expected orientation of this segment alternates with the previous one.
      repaired.push(prevVertical ? { x: b.x, y: a.y } : { x: a.x, y: b.y });
    }
    repaired.push(b);
    const beforeB = repaired[repaired.length - 2];
    prevVertical = segmentIsVertical(beforeB, b);
  }

  const cleaned = dedupeConsecutivePoints(repaired);
  if (cleaned.length < 2) return null;
  return buildPathWithCorners(cleaned, CORNER_RADIUS) || null;
}
